import React, { useCallback, useEffect, useMemo } from 'react';
import cn from 'classnames';
import DataTable, { dataTableStyles } from '@dealroadshow/uikit/core/components/Table/DataTable';
import Spinner from '@dealroadshow/uikit/core/components/Loader/Spinner';
import Paginator from '@dealroadshow/uikit/core/components/Paginator';
import Button, { ButtonVariantType } from '@dealroadshow/uikit/core/components/Button';
import { NO_POSITIONS_EMPTY_MESSAGE } from '@/finsight/ui/components/portfolioMonitor/constants';
import TableWrapper from '@/Framework/UI/Molecules/Tables/TableWrapper';
import { usePortfolioMonitorContext } from '@/finsight/application/PortfolioMonitor/PortfolioMonitorContext';
import PositionsContextProvider, {
  usePositionsContext,
} from '@/finsight/application/PortfolioMonitor/PositionsContext';
import ListTabs from '@/finsight/ui/components/portfolioMonitor/PortfolioContent/common/ListTabs';
import { usePortfoliosContext } from '@/finsight/application/PortfolioMonitor/PortfoliosContext';
import { columns } from './columns';
import BulkDeletePositions from './BulkDeletePositions';
import SearchInput from './SearchInput';
import LastUpdated from './LastUpdated';

import cardStyles from '@dealroadshow/uikit/core/styles/card.scss';
import spaceStyles from '@dealroadshow/uikit/core/styles/helpers/spaces.scss';
import styles from './positionsTable.scss';

const PositionsTable = () => {
  const { setIsFormOpened, currencies: { getCurrenciesList, collection } } = usePortfolioMonitorContext();
  const {
    selectedPortfolio,
    selectedPortfolioList,
    setSelectedPortfolioList,
  } = usePortfoliosContext();
  const {
    positionList,
    confirmDeletePositionsModal: {
      CancelChangesModal,
      isVisible,
      hideCanceledModal,
    },
    confirmDeletePositions,
    selection,
    isLoading,
  } = usePositionsContext();

  const lists = selectedPortfolio?.lists || [];
  const listHasPositions = !!(selectedPortfolioList?.id && positionList.collection?.length > 0);
  const containerClassName = listHasPositions ? styles.wrapper : styles.emptyWrapper;
  const addPositionsButtonClick = () => {
    setIsFormOpened((prev) => !prev);
  };

  const closeCanceledModal = () => {
    hideCanceledModal();
    selection.reset();
  };

  const tabLists = useMemo(() => lists.map(({ name }) => ({
    key: name, name, isDisabled: false,
  })), [lists]);

  const onChangeTab = useCallback((name) => {
    setSelectedPortfolioList(
        lists.find((item) => item.name === name),
    );
    selection.reset();
  }, [lists, selection]);

  useEffect(() => {
    if (!collection.length) {
      getCurrenciesList();
    }
  }, [collection.length]);

  const rowClassNameCallback = (data) => {
    const { collection: selectedRows } = selection;
    const isChecked = selectedRows.includes(data?.cusip);

    if (isChecked) {
      return styles.checkedRow;
    }

    return null;
  };

  return (
    <>
      <div className={ cn(cardStyles.cardContainer, spaceStyles.mbn) }>
        <div className={ styles.header }>
          <div className={ styles.headerRow }>
            <div className={ styles.headerRowLeft }>
              <h3>My Lists</h3>
              <BulkDeletePositions />
            </div>
            <div className={ styles.headerRowRight }>
              <LastUpdated />
              <div className={ styles.searchInputWrapper }>
                <SearchInput />
                <Button
                  variant={ ButtonVariantType.action }
                  dataTest="addPositionsButton"
                  onClick={ addPositionsButtonClick }
                  className={ styles.addPositionButton }
                >
                  Edit Portfolio
                </Button>
              </div>
            </div>
          </div>
          <ListTabs
            lists={ tabLists }
            selectedTab={ selectedPortfolioList.name }
            onChange={ onChangeTab }
          />
        </div>
        <TableWrapper contentClassName={ styles.tableWrapper }>
          <DataTable
            className={ cn(dataTableStyles.isHoverable, styles.hoveredRow) }
            containerClassName={ containerClassName }
            dataTest="positionsDataTable"
            columns={ columns }
            data={ selectedPortfolioList?.id ? positionList.collection : [] }
            onSortChange={ positionList.sort }
            sortOrder={ positionList.sortOrder }
            sortBy={ positionList.sortBy }
            isFetching={ positionList.isFetching || isLoading }
            loadingComponent={ Spinner }
            emptyMessage={ NO_POSITIONS_EMPTY_MESSAGE }
            showEmptyStateMessageFirst={ !selectedPortfolioList?.id }
            emptyMessageClassName={ styles.emptyMessage }
            rowClassNameCallback={ rowClassNameCallback }
          />
          { listHasPositions && (
            <Paginator
              className={ spaceStyles.pl }
              dataTest="positionsTablePagination"
              page={ positionList.page }
              perPage={ positionList.perPage }
              totalCount={ positionList.totalCount }
              onPageChange={ ({ selected }) => positionList.paginate(selected) }
              onItemsPerPageChange={ ({ value }) => positionList.setItemsPerPage(value) }
              isItemsPerPageVisible
            />
          ) }
        </TableWrapper>
      </div>
      <CancelChangesModal
        isVisible={ isVisible }
        hideCanceledModal={ closeCanceledModal }
        cancelChanges={ confirmDeletePositions }
        title="Delete Positions"
        modalTitle="Delete Positions"
        modalText="Are you sure you want to delete the selected position(s) from this list? This action is irreversible."
      />
    </>
  );
};

export default () => (
  <PositionsContextProvider>
    <PositionsTable />
  </PositionsContextProvider>
);
